import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Image,
  VStack,
  HStack,
  Checkbox,
  Divider,
  useColorModeValue,
  InputLeftElement,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormErrorMessage,
  Spinner,
  Center,
  Card,
  IconButton,
  ToastProvider,
  useToast,
} from "components-lib";
import {
  FaEye,
  FaEyeSlash,
  FaUser,
  FaLock,
  FaMobileAlt,
  FaEnvelope,
  FaWifi,
  FaSignInAlt,
} from "react-icons/fa";
import { axiosInstance } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import ImageCache from "../services/cache/ImageCache";
import "./UnifiedLogin.css";

const UnifiedLogin = () => {
  // Hooks
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: loginUser, clearError } = useAuth();

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Modals for Terms and Privacy
  const {
    isOpen: isTermsOpen,
    onOpen: onOpenTerms,
    onClose: onCloseTerms,
  } = useDisclosure();

  const {
    isOpen: isPrivacyOpen,
    onOpen: onOpenPrivacy,
    onClose: onClosePrivacy,
  } = useDisclosure();

  // Error states
  const [loginError, setLoginError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const testConnection = React.useCallback(async () => {
    try {
      setConnectionStatus("testing");

      // Use the health check endpoint if available, otherwise simulate
      try {
        const response = await axiosInstance.get("health");
        if (response.status === 200) {
          setConnectionStatus("connected");
        }
        toast({
          title: "Server Connected",
          description: response.data?.message || "Server is up and running",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } catch (healthError) {
        // If health check fails, try a simple GET to the base URL
        try {
          await axiosInstance.get("");
          setConnectionStatus("connected");
          toast({
            title: "Server Connected",
            description: "Successfully connected to the server",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        } catch (fallbackError) {
          throw new Error("Unable to connect to the server");
        }
      }
    } catch (error) {
      console.error("Connection test failed:", error);
      setConnectionStatus("failed");
      toast({
        title: "Connection Failed",
        description:
          error.message ||
          "Unable to connect to the server. Please check if the server is running.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }, [toast]);

  // Check for session expiration or other URL parameters
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Clear any previous auth errors
    clearError();

    // Check for session expiration
    if (params.get("session") === "expired") {
      toast({
        title: "Session Expired",
        description: "Your session has expired. Please log in again.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });

      // Clean up the URL
      navigate("/login", { replace: true });
    }

    // Auto-test connection on component mount
    testConnection();
  }, [location.search, navigate, toast, clearError, testConnection]);

  // Handle login with email/password
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors
    setLoginError("");
    setEmailError("");
    setPasswordError("");

    // Validate form
    let isValid = true;

    // Email validation
    if (!email.trim()) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    }

    // Password validation
    if (!password) {
      setPasswordError("Password is required");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      isValid = false;
    }

    if (!isValid) {
      // Show error toast if validation fails
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setLoading(true);

    try {
      // Make login API call
      const response = await axiosInstance.post("api/login", {
        email: email.trim().toLowerCase(),
        password: password.trim(),
      });

      // get server response
      const { token, user: serverUser } = response.data;

      // ensure we have a user object
      const userData = serverUser || {};

      // try to decode token and extract role(s)
      try {
        const payload = decodeJwt(token);
        if (payload) {
          // common role keys in tokens
          const extractedRole =
            payload.role ||
            payload.role_name ||
            payload.user_role ||
            payload.roles ||
            (payload.realm_access &&
              payload.realm_access.roles &&
              payload.realm_access.roles[0]) ||
            null;

          // attach role to userData if not present, prefer server value if present
          if (extractedRole && !userData.role) {
            userData.role = Array.isArray(extractedRole)
              ? extractedRole[0]
              : extractedRole;
          }

          // keep full token payload for other parts of the app if needed
          userData.tokenPayload = payload;
        }
      } catch (e) {
        // ignore token decode errors and continue with server userData
      }

      if (!token || !userData) {
        throw new Error("Invalid response from server");
      }

      // Store remember me preference
      // if (rememberMe) {
      //   localStorage.setItem('rememberMe', 'true');
      // } else {
      //   localStorage.removeItem('rememberMe');
      // }

      // console.log('user data', userData)
      // Use the login function from AuthContext
      const loginSuccess = await loginUser(token, userData);

      if (!loginSuccess) {
        throw new Error("Failed to initialize user session");
      }

      // const profileRes = localStorage.getItem('user')
      // if (profileRes.clinic_icon
      // ) {
      //   const clinic_iconData = await fetch(clinic_icon
      //   );
      //   if (clinic_iconData) {
      //     localStorage.setItem('clinic_icondata', clinic_iconData);
      //   } 
      // }

      // Clear previous failures on fresh login
      try {
        if (ImageCache.failed) ImageCache.failed.clear();
      } catch (_) { }

      // Prefetch critical assets (Fire and forget)
      const userRole = (userData.role || "").toLowerCase();

      // Start prefetching in background
      (async () => {
        try {
          const promises = [];

          // 1. Prefetch kifyati logo if not already cached
          promises.push(ImageCache.get("/kifayti_logo.png"));

          // 2. If doctor/admin, fetch profile to get signature/logo
          if (['doctor', 'admin'].includes(userRole)) {
            try {
              const profileRes = localStorage.getItem('user')
              console.log('profileRes', profileRes)
              if (profileRes) {
                const { doctor_signature, clinic_logo, doctor_logo, profile_image, qr_code } = profileRes || {};
                // Persist URLs for reference
                // if (clinic_logo) localStorage.setItem('clinic_logo', clinic_logo);
                // if (doctor_logo) localStorage.setItem('doctor_logo', doctor_logo);
                // if (doctor_signature) localStorage.setItem('doctor_signature', doctor_signature);
                // if (profile_image) localStorage.setItem('profile_image', profile_image);
                // if (qr_code) localStorage.setItem('qr_code', qr_code);




                // Fetch and store BASE64 data for immediate synchronous access
                // if (signature) {
                //   promises.push(ImageCache.get(signature).then(data => {
                //     if (data) localStorage.setItem('signature_data', data);
                //   }));
                // }
                // if (clinic_logo) {
                //   promises.push(ImageCache.get(clinic_logo).then(data => {
                //     if (data) localStorage.setItem('clinic_logo_data', data);
                //   }));
                // }
                // if (doctor_logo) {
                //   promises.push(ImageCache.get(doctor_logo).then(data => {
                //     if (data) localStorage.setItem('doctor_logo_data', data);
                //   }));
                // }
                // if (profile_image) {
                //   promises.push(ImageCache.get(profile_image).then(data => {
                //     if (data) localStorage.setItem('profile_image_data', data);
                //   }));
                // }
                // if (qr_code) {
                //   promises.push(ImageCache.get(qr_code).then(data => {
                //     if (data) localStorage.setItem('qr_code_data', data);
                //   }));
                // }
              }
            } catch (e) {
              // ignore profile fetch error
            }
          }

          await Promise.allSettled(promises);
          console.log('Assets prefetch initiated');
        } catch (e) {
          console.warn('Asset prefetch error', e);
        }
      })();

      // Show success message
      toast({
        title: "Login successful",
        description: `Welcome back, ${userData.firstname || userData.email || "User"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      // Get redirect path based on user role
      const roleToPath = {
        admin: "/admin",
        doctor: "/admin/doctor",
        front_desk: "/admin/appointments",
        frontdesk: "/admin/appointments",
        user: "/dashboard",
        patient: "/patient/dashboard",
        "": "/", // Fallback for unknown roles
      };

      // Get redirect path from URL or use role-based default
      const searchParams = new URLSearchParams(location.search);
      const redirectPath =
        searchParams.get("redirect") || roleToPath[userRole] || "/";

      // Small delay to allow toast to be seen before redirect
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 500);
    } catch (error) {
      console.error("Login error:", error);

      let errorMessage = "Login failed. Please try again.";

      if (error.response) {
        // Server responded with an error status code
        if (error.response.status === 401) {
          errorMessage = "Invalid email or password";
        }
        else if (error.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }
        else if (error.response.status === 403) {
          errorMessage = "Access denied. Your Clinic is locked. Contact support.";
        }
        else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // No response received
        errorMessage =
          "Unable to connect to the server. Please check your internet connection.";
      } else if (error.message) {
        // Other errors (e.g., invalid response format)
        errorMessage = error.message;
      }

      setLoginError(errorMessage);

      // Show error toast
      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });

      // Clear password field on error
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  // helper to decode JWT payload safely
  const decodeJwt = (token) => {
    try {
      const parts = token.split(".");
      if (parts.length < 2) return null;
      const payload = parts[1];
      // atob may not handle URL-safe base64, normalize it
      const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
      const json = decodeURIComponent(
        atob(padded)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  };

  return (
    <Flex
      className="bg-gradient-to-r from-blue-500 to-teal-500"
      minH="100vh"
      align="center"
      justify="center"
      p={4}
    >
      <div className="login-card" style={{ backgroundColor: 'white' }}>
        <div className="login-header">
          <Image src="/kifayti_logo.png" alt="Kifyati Health Logo" className="login-header-logo" />
          <h1 className="login-header-title">Welcome Back</h1>
          <p className="login-header-subtitle">Sign in to access your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {/* Email Field */}
          <div className="form-group">
            <label className="form-label">Email address</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <FaEnvelope size={16} />
              </span>
              <input
                type="email"
                className="input-field"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
            {emailError && <div className="error-message">{emailError}</div>}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <FaLock size={16} />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="input-icon-right"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {passwordError && <div className="error-message">{passwordError}</div>}
          </div>

          {loginError && (
            <div className="error-message" style={{ textAlign: 'center', marginTop: '12px' }}>
              {loginError}
            </div>
          )}

          <button
            type="submit"
            className="sign-in-button"
            disabled={loading}
          >
            <FaSignInAlt size={16} />
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p className="terms-text">
            By signing in, you agree to our{' '}
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#4649ff',
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 1000,
              }}
              onClick={onOpenTerms}
            >
              Terms of Service
            </button>{' '}
            and{' '}
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#4649ff',
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 1000,
              }}
              onClick={onOpenPrivacy}
            >
              Privacy Policy
            </button>
            .
          </p>
        </form>

        {/* Terms Modal */}
        <Modal isOpen={isTermsOpen} onClose={onCloseTerms} size="4xl" maxHeight="90vh" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader >Terms of Service</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <iframe
                title="Terms of Service"
                src="/terms"
                style={{ width: '100%', height: '70vh', maxHeight: '75vh', border: 'none' }}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Privacy Modal */}
        <Modal isOpen={isPrivacyOpen} onClose={onClosePrivacy} size="4xl" maxHeight="90vh" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Privacy Policy</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <iframe
                title="Privacy Policy"
                src="/privacy"
                style={{ width: '100%', height: '70vh', maxHeight: '75vh', border: 'none' }}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        <div className="login-footer">
          <button
            className="test-connection-btn"
            onClick={testConnection}
            disabled={connectionStatus === "testing"}
            type="button"
          >
            <FaWifi size={14} />
            {connectionStatus === "connected"
              ? "Connected"
              : connectionStatus === "failed"
                ? "Connection Failed"
                : "Test Connection"}
          </button>
          {connectionStatus === "connected" && (
            <span className="connection-status online">Server is online</span>
          )}
        </div>
      </div>
    </Flex>
  );
};

export default function WrappedUnifiedLogin() {
  return (
    <ToastProvider>
      <UnifiedLogin />
    </ToastProvider>
  );
}
